import type { ResolveDistrictResult } from '@citizen-walk/shared-types';
import { useEffect, useRef } from 'react';

/**
 * Sprint AP (UI5 多地区 PWA 識別 / docs/16 §10-2 P3 H8):
 *   `/m/:urlToken` で district が ready になったら、runtime で manifest JSON を生成し
 *   Blob URL に変換 → `<link rel="manifest">` の href を差し替える。
 *
 * 旧 v1 は「動的 manifest 差替えは廃止 (アプリ名は単一固定)」方針 (`old/apps/user/src/contexts/DistrictContext.tsx` §11
 * コメント踏襲)。本 sprint は v2 docs/16 §10-2 v1.1 で「**部分刷新** — 地区別アプリ名対応」と
 * 再導入が決定されたため、新規実装 (L0 相当)。
 *
 * iOS Safari / Android Chrome の挙動:
 *   - install 前 (まだ「ホーム画面に追加」を押していない時点) で manifest が district 別になっていれば
 *     OS は district 別 name / id でホーム画面 entry を作る → AC「2 地区追加時に独立アイコン + 別 name」を達成
 *   - install 後の manifest 差替えは iOS Safari の PWA cache が固定するため反映されない
 *     → AC は install 時点で district 別 manifest が見えていることが前提
 *
 * unmount or district 変更時に古い blob URL を URL.revokeObjectURL する。
 * vite-plugin-pwa が build 時に inject する `<link rel="manifest">` を find して href を書き換える。
 * link 要素自体が存在しない (HTML 直書きされていない) ケースでは新規 append する (dev mode 等)。
 */

/**
 * vite.config.ts (apps/user/vite.config.ts) の manifest 設定と一致させる静的フィールド。
 * 動的化するのは name / short_name / start_url / scope / id のみで、icons / 色 / 言語 / display
 * モード等は district 横断で共通。値が乖離すると build 時 default manifest と runtime override で
 * UX が不整合になるため定数として集約。
 */
const STATIC_MANIFEST_FIELDS = {
  description: '参加型まち歩きプラットフォーム',
  display: 'standalone',
  orientation: 'portrait',
  background_color: '#ffffff',
  theme_color: '#0d9488',
  lang: 'ja',
  icons: [
    {
      src: '/pwa-icon.svg',
      sizes: 'any',
      type: 'image/svg+xml',
      purpose: 'any',
    },
    {
      src: '/pwa-icon-maskable.svg',
      sizes: 'any',
      type: 'image/svg+xml',
      purpose: 'maskable',
    },
  ],
} as const;

/**
 * iOS Safari home screen の short_name 表示は概ね 12 文字程度で truncate されるため、
 * 先頭 12 文字に切り詰める。漢字 / 仮名前提なので surrogate pair 考慮は不要 (Array.from で
 * code point 単位を保証)。
 */
function truncateShortName(name: string, max = 12): string {
  const chars = Array.from(name);
  if (chars.length <= max) return name;
  return chars.slice(0, max).join('');
}

export interface DistrictManifestInput {
  districtName: string;
  urlToken: string;
}

/**
 * manifest JSON を構築する純粋関数 (test 容易性のため export)。
 * `id` は iOS PWA / Android Chrome (W3C App Identity) が「別 install」として区別する key。
 * urlToken を含む path にすることで 2 地区追加時に独立 entry が作られる。
 */
export function buildDistrictManifest({ districtName, urlToken }: DistrictManifestInput) {
  const scope = `/m/${urlToken}/`;
  return {
    name: districtName,
    short_name: truncateShortName(districtName),
    description: STATIC_MANIFEST_FIELDS.description,
    start_url: scope,
    scope,
    id: scope,
    display: STATIC_MANIFEST_FIELDS.display,
    orientation: STATIC_MANIFEST_FIELDS.orientation,
    background_color: STATIC_MANIFEST_FIELDS.background_color,
    theme_color: STATIC_MANIFEST_FIELDS.theme_color,
    lang: STATIC_MANIFEST_FIELDS.lang,
    icons: STATIC_MANIFEST_FIELDS.icons.map((i) => ({ ...i })),
  };
}

/**
 * `<link rel="manifest">` を find or create。
 * vite-plugin-pwa が build 時に inject するが、dev mode (devOptions.enabled: false) では存在しない
 * ため、欠けていれば head に append する。
 */
function ensureManifestLink(doc: Document): HTMLLinkElement {
  let link = doc.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (link) return link;
  link = doc.createElement('link');
  link.rel = 'manifest';
  doc.head.appendChild(link);
  return link;
}

/**
 * district 解決後に dynamic manifest を `<link rel="manifest">` に差し込む hook。
 *
 * - district = null/undefined のときは何もしない (default static manifest が有効のまま)
 * - district 変更時に旧 blob URL を URL.revokeObjectURL
 * - unmount 時に旧 blob URL を revoke し、`<link>` の href を元の static manifest path に戻す
 *
 * 注意: link 自身は document の head に既にある (vite-plugin-pwa inject) ので、href だけ書き換える。
 * 元の href は cleanup 用に保持して unmount 時に復元する。
 */
export function useDistrictManifest(district: ResolveDistrictResult | null | undefined): void {
  // 直前に発行した blob URL (revoke 用)。React state ではなく ref で十分 (render に影響しない)。
  const prevBlobUrlRef = useRef<string | null>(null);
  // 初回マウント時の元 href (cleanup で復元、SPA navigation で `/m/:urlToken` を抜けた時に
  // 静的 manifest に戻すため)。
  const originalHrefRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      typeof document === 'undefined' ||
      typeof URL === 'undefined' ||
      typeof Blob === 'undefined'
    ) {
      // SSR / 非対応環境ガード (本 app は CSR のみだが防御的に)
      return;
    }
    if (!district) {
      return;
    }

    const link = ensureManifestLink(document);
    if (originalHrefRef.current === null) {
      // 初回適用前の static manifest path を保存 (link を新規生成した場合は '' のまま)
      originalHrefRef.current = link.getAttribute('href') ?? '';
    }

    const manifest = buildDistrictManifest({
      districtName: district.districtName,
      urlToken: district.urlToken,
    });
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    const blobUrl = URL.createObjectURL(blob);

    // 旧 blob URL を revoke してから新規を当てる。順序は href 書換後に revoke すべき
    // (link.href がまだ旧 blob を指しているとブラウザが fetch 中の可能性) ため、
    // 旧 URL の参照を保持してから書き換え → 旧 URL を revoke。
    const oldBlobUrl = prevBlobUrlRef.current;
    link.setAttribute('href', blobUrl);
    prevBlobUrlRef.current = blobUrl;
    if (oldBlobUrl) {
      URL.revokeObjectURL(oldBlobUrl);
    }

    return () => {
      const url = prevBlobUrlRef.current;
      if (url) {
        URL.revokeObjectURL(url);
        prevBlobUrlRef.current = null;
      }
      // 元 href が判明していれば static manifest path に復元
      // (空文字 = link を新規生成した場合は何もしない)
      const original = originalHrefRef.current;
      if (original) {
        link.setAttribute('href', original);
      }
    };
    // district オブジェクト全体ではなく districtName / urlToken のみを deps に入れて
    // resolver の cache→remote 切替 (内容同一でも参照差) で不要 re-run しないようにする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [district?.districtName, district?.urlToken]);
}
